class LoadMorePagination extends HTMLElement {
  constructor() {
    super();
    this.refreshReferences();
    this.originalScrollIntoView = Element.prototype.scrollIntoView;
    // Bind the saveScrollPosition method to this instance
    this.saveScrollPosition = this.saveScrollPosition.bind(this);
  }

  refreshReferences() {
    this.loadMoreContainer = this.querySelector(".load-more-container");
    this.loadMoreButton = this.querySelector("#load-more-button");

    // If loadMoreButton doesn't exist, set the other fields to empty and return
    if (!this.loadMoreButton) {
      this.parentSelector = "";
      this.childSelector = "";
      this.dataContainer = null;
      this.dataSets = [];
      this.progressText = null;
      this.progressBarFill = null;
      this.itemsPerPage = 0;
      return;
    }

    this.parentSelector = this.loadMoreButton?.getAttribute("data-parent-selector") || "";
    this.childSelector = this.loadMoreButton?.getAttribute("data-child-selector") || "";
    this.dataContainer = this.querySelector(`.${this.parentSelector}`);
    this.dataSets = this.querySelectorAll(`.${this.childSelector}`);
    this.progressText = this.querySelector("#progress-text");
    this.progressBarFill = this.querySelector("#progress-bar-fill");

    this.itemsPerPage = this.loadMoreButton?.dataset.itemsPerPage;

    if (this.itemsPerPage) {
      this.itemsPerPage = Number(this.itemsPerPage);
    }
  }

  async connectedCallback() {
    // Prevent browser from restoring scroll position automatically
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    window.scrollTo({ top: 0, behavior: "auto" });

    // Save scroll position on beforeunload and pagehide (Safari compatibility)
    window.addEventListener('beforeunload', this.saveScrollPosition);
    window.addEventListener('pagehide', this.saveScrollPosition);

    this.refreshReferences();
    this.initializeLoadMore();

    document.addEventListener("dataSets:updated", async () => {
      await this.handleUpdateLoadMore();
    });

    await this.loadInitialPages();
  }

  saveScrollPosition() {
    try {
      history.replaceState(
        { ...history.state, scroll: { x: window.scrollX, y: window.scrollY } },
        document.title
      );
      sessionStorage.setItem('scroll-x', window.scrollX);
      sessionStorage.setItem('scroll-y', window.scrollY);
    } catch (e) {
      // Safari private mode may throw, ignore
    }
  }

  async loadInitialPages() {
    if (!this.loadMoreButton || !this.dataContainer) return;

    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get("page") || "1");
    let totalPages = parseInt(this.loadMoreButton.getAttribute("data-total-pages"));
    this.enableLoading();
    if (this.dataContainer && currentPage > 1) {
      this.refreshReferences();
      this.addSkeletons();

      // Remove existing data sets
      this.dataSets.forEach((data) => {
        this.dataContainer.removeChild(data);
      });

      for (let p = 1; p <= currentPage; p++) {
        const url = new URL(window.location.href);
        url.searchParams.set("page", p);
        try {
          const response = await fetch(`${url.pathname}${url.search}`);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newDataSets = doc.querySelectorAll(`.${this.childSelector}`);
          newDataSets.forEach((data) => {
            this.dataContainer.appendChild(data);
          });
          // Remove skeletons after loading
          this.hideSkeletons();
        } catch (e) {
          console.error("Error loading datas for page", p, e);
        }
      }
    }
    setTimeout(() => {
      this.updateProgressBar();
      this.disableLoading();
      if (currentPage === totalPages) {
        this.loadMoreButton.style.display = "none";
      }
      this.initDefaultScrollIntoView();
    }, 200);
  }

  initDefaultScrollIntoView() {
    // Enable scrollIntoView after all pages are loaded
    Element.prototype.scrollIntoView = this.originalScrollIntoView;

    // Restore scrollRestoration to auto for future navigations
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'auto';
    }

    // --- Only restore scroll position if navigation is back/forward ---
    let shouldRestoreScroll = false;
    if (performance && performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType("navigation");
      if (navEntries.length > 0) {
        shouldRestoreScroll = navEntries[0].type === "back_forward";
      } else if (performance.navigation) {
        // Fallback for older browsers
        shouldRestoreScroll = performance.navigation.type === 2;
      }
    }

    if (shouldRestoreScroll) {
      let scroll = history.state && history.state.scroll;
      // Fallback for Safari: use sessionStorage if history.state.scroll is missing
      if (
        (!scroll || typeof scroll.x === "undefined" || typeof scroll.y === "undefined") &&
        sessionStorage.getItem('scroll-x') !== null &&
        sessionStorage.getItem('scroll-y') !== null
      ) {
        scroll = {
          x: parseInt(sessionStorage.getItem('scroll-x'), 10),
          y: parseInt(sessionStorage.getItem('scroll-y'), 10)
        };
      }
      if (scroll && typeof scroll.x === "number" && typeof scroll.y === "number") {
        window.scrollTo({
          top: scroll.y,
          left: scroll.x,
          behavior: "smooth"
        });
        return;
      }
    }
    // Default: scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  addSkeletons() {
    // Show skeletons while loading
    if (window.location.pathname.includes('/account')) {
      this.showSkeletonsOrders();
    } else if (window.location.pathname.includes('/blogs')) {
      this.showSkeletonsBlogs();
    } else {
      this.showSkeletons();
    }
  }

  showSkeletons() {
    // Adjust the count as needed
    const skeletonCount = this.itemsPerPage || 8;
    this.skeletonElements = [];
    for (let i = 0; i < skeletonCount; i++) {
      const skeleton = document.createElement("li");
      skeleton.className = "product-card-skeleton grid__item";
      skeleton.innerHTML = `
        <div class="skeleton-image"></div>
        <div class="skeleton-title"></div>
        <div class="skeleton-price"></div>
      `;
      this.dataContainer.appendChild(skeleton);
      this.skeletonElements.push(skeleton);
    }
  }

  showSkeletonsBlogs() {
    // Adjust the count as needed
    const skeletonCount = this.itemsPerPage || 6;
    this.skeletonElements = [];
    for (let i = 0; i < skeletonCount; i++) {
      const skeleton = document.createElement("li");
      skeleton.className = "blogs-posts-skeleton grid__item";
      skeleton.innerHTML = `
        <div class="skeleton-image"></div>
        <div class="skeleton-title"></div>
        <div class="skeleton-price"></div>
      `;
      this.dataContainer.appendChild(skeleton);
      this.skeletonElements.push(skeleton);
    }
  }

  showSkeletonsOrders() {
    // Adjust the count as needed
    const skeletonCount = this.itemsPerPage || 4;
    this.skeletonElements = [];
    for (let i = 0; i < skeletonCount; i++) {
      const skeleton = document.createElement("tr");
      skeleton.className = "account-orders-skeleton";
      skeleton.innerHTML = `
        <td id="RowOrder" role="cell" headers="ColumnOrder">
        </td>
        <td headers="RowOrder ColumnDate" role="cell">
          <time datetime="2025-05-20T05:58:09Z"></time>
        </td>
        <td headers="RowOrder ColumnPayment" role="cell">
        </td>
        <td headers="RowOrder ColumnFulfillment" role="cell">
        </td>
        <td headers="RowOrder ColumnTotal" role="cell">
        </td>

      `;
      this.dataContainer.appendChild(skeleton);
      this.skeletonElements.push(skeleton);
    }
  }

  hideSkeletons() {
    if (this.skeletonElements) {
      this.skeletonElements.forEach(el => el.remove());
      this.skeletonElements = [];
    }
  }

  initializeLoadMore() {
    this.addEventListener("click", (event) => {
      const button = event.target.closest("#load-more-button");
      if (!button) return;
      this.enableLoading();
      this.addSkeletons();

      let currentPage = parseInt(button.getAttribute("data-current-page"));
      let totalPages = parseInt(button.getAttribute("data-total-pages"));

      if (!isNaN(currentPage) && !isNaN(totalPages)) {
        if (currentPage < totalPages) {
          const nextPage = currentPage + 1;
          const url = new URL(window.location.href);
          url.searchParams.set("page", nextPage);

          fetch(`${url.pathname}${url.search}`)
            .then((response) => response.text())
            .then((html) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const newDataSets = doc.querySelectorAll(`.${this.childSelector}`);

              newDataSets.forEach((data) => {
                this.dataContainer.appendChild(data);
                this.hideSkeletons();
              });
              button.setAttribute("data-current-page", nextPage);

              history.replaceState(null, "", url);

              this.updateProgressBar();
              this.disableLoading();
              if (nextPage >= totalPages) {
                button.style.display = "none";
              }
            })
            .catch((error) =>
              console.error("Error loading more datas:", error)
            );
        }
        return;
      }
    });
  }

  async handleUpdateLoadMore() {
    this.refreshReferences();
    this.enableLoading();
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get("page") || "1");

    if (this.dataContainer) {
      this.addSkeletons();
      this.dataSets.forEach((data) => {
        this.dataContainer.removeChild(data);
      });
      for (let p = 1; p <= currentPage; p++) {
        const url = new URL(window.location.href);
        url.searchParams.set("page", p);
        try {
          const response = await fetch(`${url.pathname}${url.search}`);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newDataSets = doc.querySelectorAll(`.${this.childSelector}`);
          newDataSets.forEach((data) => {
            if (
              !this.dataContainer.querySelector(
                `[data-id="${data.getAttribute("data-id")}"]`
              )
            ) {
              this.dataContainer.appendChild(data);
            }
            this.hideSkeletons();
          });
        } catch (e) {
          console.error("Error loading datas for page", p, e);
        }
      }
    }

    setTimeout(() => {
      this.reRanderPagination();
      this.disableLoading();
    }, 200);
  }

  enableLoading() {
    if (this.loadMoreContainer) {
      this.loadMoreContainer.classList.add("opacity-5");
    }
    if (this.loadMoreButton) {
      this.loadMoreButton.disabled = true;
    }
  }

  disableLoading() {
    if (this.loadMoreContainer) {
      this.loadMoreContainer.classList.remove("opacity-5");
    }
    if (this.loadMoreButton) {
      this.loadMoreButton.disabled = false;
    }
  }

  reRanderPagination() {
    const url = new URL(window.location.href);
    const prevElement = this.querySelector(".load-more-container");
    fetch(`${url.pathname}${url.search}`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newElement = doc.querySelector(".load-more-container");
        if (newElement) {
          prevElement.innerHTML = newElement.innerHTML;
        }
        this.refreshReferences();
        this.updateProgressBar();
      })
      .catch((error) => console.error("Error loading more datas:", error));
  }

  updateProgressBar() {
    if (!this.dataContainer || !this.progressText || !this.progressBarFill)
      return;
    const displayedDataSetsCount = this.dataContainer.querySelectorAll(
      `.${this.childSelector}`
    ).length;
    let totalDataSetsCount =
      parseInt(this.progressText.getAttribute("data-total")) ||
      displayedDataSetsCount;
    if (displayedDataSetsCount > totalDataSetsCount) {
      totalDataSetsCount = displayedDataSetsCount;
      this.progressText.setAttribute("data-total", totalDataSetsCount);
    }
    this.progressText.textContent = `${displayedDataSetsCount} out of ${totalDataSetsCount} ${this.loadMoreButton.getAttribute("data-items")}`;
    this.progressBarFill.style.width =
      totalDataSetsCount > 0
        ? `${(displayedDataSetsCount / totalDataSetsCount) * 100}%`
        : "0%";
  }
}

customElements.define("load-more-pagination", LoadMorePagination);